import React, { useState, useEffect } from 'react';
import { CreditCard, Key, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, TestTube, Info } from 'lucide-react';
import api from '../../api/api';

const TenantPaymentGateway = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [settings, setSettings] = useState({
    razorpay_enabled: false,
    razorpay_key_id: '',
    razorpay_key_secret: '',
    razorpay_test_mode: true,
    upi_enabled: false,
    upi_id: '',
    bank_transfer_enabled: false,
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/tenant-payment-gateway/settings');
      setSettings({
        razorpay_enabled: response.data.razorpay_enabled || false,
        razorpay_key_id: response.data.razorpay_key_id || '',
        razorpay_key_secret: response.data.razorpay_key_secret_masked || '',
        razorpay_test_mode: response.data.razorpay_test_mode !== false,
        upi_enabled: response.data.upi_enabled || false,
        upi_id: response.data.upi_id || '',
        bank_transfer_enabled: response.data.bank_transfer_enabled || false,
        bank_name: response.data.bank_name || '',
        account_number: response.data.account_number || '',
        ifsc_code: response.data.ifsc_code || '',
        account_holder_name: response.data.account_holder_name || '',
        has_secret: response.data.has_secret || false
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/tenant-payment-gateway/settings', settings);
      setMessage({ 
        type: 'success', 
        text: response.data.configured 
          ? '✅ Payment gateway configured successfully! Online payments are now enabled.'
          : '✅ Settings saved. Complete all required fields to enable online payments.'
      });
      fetchSettings();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/tenant-payment-gateway/test-connection');
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payment Gateway Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Configure online payment to receive fees directly in your bank account</p>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">How it works:</p>
          <ul className="mt-1 space-y-1 text-blue-700">
            <li>• Configure your Razorpay account keys below</li>
            <li>• Students can pay fees online through Student Portal</li>
            <li>• Payments go directly to your linked bank account</li>
            <li>• Get free Razorpay account at <a href="https://razorpay.com" target="_blank" rel="noopener noreferrer" className="underline">razorpay.com</a></li>
          </ul>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Razorpay Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6" />
            <div>
              <h2 className="font-semibold">Razorpay Configuration</h2>
              <p className="text-sm text-blue-100">Accept payments via Cards, UPI, Net Banking, Wallets</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Enable Online Payment</p>
              <p className="text-sm text-gray-500">Allow students to pay fees online</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.razorpay_enabled}
                onChange={(e) => setSettings({ ...settings, razorpay_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* API Keys */}
          <div className={`space-y-4 ${!settings.razorpay_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key className="inline h-4 w-4 mr-1" />
                Key ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={settings.razorpay_key_id}
                onChange={(e) => setSettings({ ...settings, razorpay_key_id: e.target.value })}
                placeholder="rzp_test_xxxxxxxxxxxx"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Found in Razorpay Dashboard → Settings → API Keys</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key className="inline h-4 w-4 mr-1" />
                Key Secret <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={settings.razorpay_key_secret}
                  onChange={(e) => setSettings({ ...settings, razorpay_key_secret: e.target.value })}
                  placeholder={settings.has_secret ? "Enter new secret to change" : "Enter your key secret"}
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecret ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {settings.has_secret && (
                <p className="text-xs text-green-600 mt-1">✓ Secret key is saved. Leave blank to keep existing.</p>
              )}
            </div>

            {/* Test Mode */}
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <input
                type="checkbox"
                checked={settings.razorpay_test_mode}
                onChange={(e) => setSettings({ ...settings, razorpay_test_mode: e.target.checked })}
                className="w-4 h-4 text-yellow-600"
              />
              <div>
                <p className="text-sm font-medium text-yellow-800">Test Mode</p>
                <p className="text-xs text-yellow-700">Use test keys for testing. Uncheck for live payments.</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleTestConnection}
              disabled={testing || !settings.razorpay_enabled || !settings.razorpay_key_id}
              className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4" />
              )}
              Test Connection
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default TenantPaymentGateway;
